import React, { useState, useEffect, useRef, createContext, useContext } from 'react';
import OCApi from '../api/client.js';
import {
  DEFAULT_SETTINGS, DEFAULT_INTERESTS, OC_LS_KEY, isEmployerRole, ocLoad, routeFromPath,
} from '../constants/index.js';

const StoreContext = createContext(null);

function useStore() {
    const context = useContext(StoreContext);
    if (!context) {
        throw new Error('useStore must be used within a StoreProvider');
    }
    return context;
}

function StoreProvider({ children }) {
    const saved0 = ocLoad();
    const [theme, setThemeState] = useState(saved0.theme || 'light');
    const [route, setRoute] = useState(
        () => routeFromPath(window.location.pathname) || saved0.route || { name: 'home' }
    );
    const [saved, setSaved] = useState(saved0.saved || []);
    const [savedDbMap, setSavedDbMap] = useState(saved0.savedDbMap || {});
    const [applied, setApplied] = useState(saved0.applied || []);
    const [interests, setInterests] = useState(saved0.interests || DEFAULT_INTERESTS);
    const [settings, setSettings] = useState({ ...DEFAULT_SETTINGS, ...(saved0.settings || {}) });
    const [authed, setAuthed] = useState(false);
    const [authReady, setAuthReady] = useState(false);
    const [user, setUser] = useState({ name: '', email: '', alertsPaused: false });
    const [query, setQuery] = useState('');
    const [toast, setToast] = useState(null);
    const [apply, setApply] = useState(null);
    const [isEmployerMode, setEmployerMode] = useState(saved0.isEmployerMode || false);
    const [opportunities, setOpportunities] = useState([]);
    const [oppCache, setOppCache] = useState({});
    const [oppLoading, setOppLoading] = useState(false);
    const [oppRefreshing, setOppRefreshing] = useState(false);
    const [oppLoadError, setOppLoadError] = useState('');
    const oppFetchKeyRef = useRef('');
    const oppFetchInflightRef = useRef(null);
    const oppCountRef = useRef(0);
    const [categories, setCategories] = useState([]);
    const [defaultSourceId, setDefaultSourceId] = useState(null);
    const [savedSearches, setSavedSearches] = useState([]);
    const [alertHistory, setAlertHistory] = useState([]);
    const [employerProfile, setEmployerProfile] = useState(null);
    const [employerListings, setEmployerListings] = useState([]);
    const [employerApplicants, setEmployerApplicants] = useState([]);
    const [employerStats, setEmployerStats] = useState(null);
    const [employerLoading, setEmployerLoading] = useState(false);

    const mergeOpportunities = (list) => {
        if (!list || !list.length) return;
        setOpportunities(prev => {
            const map = new Map(prev.map(o => [o.id, o]));
            list.forEach(o => map.set(o.id, o));
            return Array.from(map.values());
        });
        setOppCache(prev => {
            const next = { ...prev };
            list.forEach(o => { next[o.id] = o; });
            return next;
        });
        setSavedDbMap(prev => {
            const next = { ...prev };
            list.forEach(o => { if (o.dbId) next[o.id] = o.dbId; });
            return next;
        });
    };

    useEffect(() => {
        oppCountRef.current = opportunities.length;
    }, [opportunities]);

    useEffect(() => {
        localStorage.setItem(OC_LS_KEY, JSON.stringify({
            theme, route, saved, savedDbMap, applied, interests, settings, isEmployerMode,
        }));
    }, [theme, route, saved, savedDbMap, applied, interests, settings, isEmployerMode]);

    const toastTimer = useRef(null);
    const showToast = (message, icon) => {
        setToast({ message, icon, key: Date.now() });
        if (toastTimer.current) clearTimeout(toastTimer.current);
        toastTimer.current = setTimeout(() => setToast(null), 2600);
    };

    const stableOppParamsKey = (params) => {
        const normalized = {};
        Object.keys(params || {}).sort().forEach((k) => {
            const v = params[k];
            if (v === undefined || v === null || v === '') return;
            if (k === 'deadline_after' || k === 'deadline_before') {
                normalized[k] = String(v).slice(0, 10);
                return;
            }
            normalized[k] = v;
        });
        return JSON.stringify(normalized);
    };

    const loadOpportunities = async (params = {}, options = {}) => {
        const { force = false, background } = options;
        const key = stableOppParamsKey(params);

        if (!force && oppFetchKeyRef.current === key && !oppFetchInflightRef.current) {
            return null;
        }
        if (!force && oppFetchInflightRef.current?.key === key) {
            return oppFetchInflightRef.current.promise;
        }

        const run = async () => {
            const useBackground = background ?? oppCountRef.current > 0;
            setOppLoadError('');
            if (useBackground) {
                setOppRefreshing(true);
            } else {
                setOppLoading(true);
            }
            try {
                const data = await OCApi.getOpportunities(params);
                mergeOpportunities(data.opportunities || []);
                oppFetchKeyRef.current = key;
                return data.opportunities || [];
            } catch (err) {
                const message = err.message || 'Could not load opportunities';
                setOppLoadError(message);
                if (!useBackground) showToast(message, 'x');
                return [];
            } finally {
                setOppLoading(false);
                setOppRefreshing(false);
            }
        };

        const promise = run();
        oppFetchInflightRef.current = { key, promise };
        try {
            return await promise;
        } finally {
            if (oppFetchInflightRef.current?.promise === promise) {
                oppFetchInflightRef.current = null;
            }
        }
    };

    const fetchOpportunity = async (publicId) => {
        if (oppCache[publicId]) return oppCache[publicId];
        try {
            const op = await OCApi.getOpportunity(publicId);
            if (op) mergeOpportunities([op]);
            return op;
        } catch (err) {
            return null;
        }
    };

    const loadSavedFromApi = async () => {
        try {
            const data = await OCApi.getSaved();
            const rows = (data.saved || []).map(row => OCApi.mapOpportunity({
                id: row.opportunityId,
                publicId: row.publicId,
                title: row.title,
                organization: row.organization,
                description: row.description,
                countryCode: row.countryCode,
                city: row.city,
                isRemote: row.isRemote,
                locationRaw: row.locationRaw,
                deadline: row.deadline,
                applyUrl: row.applyUrl,
                categorySlug: row.categorySlug,
                sourceName: row.sourceName,
                status: row.status,
                createdAt: row.oppCreatedAt,
            })).filter(Boolean);
            mergeOpportunities(rows);
            setSaved(rows.map(o => o.id));
            const dbMap = {};
            (data.saved || []).forEach(row => {
                if (row.publicId && row.opportunityId) dbMap[row.publicId] = row.opportunityId;
            });
            setSavedDbMap(dbMap);
        } catch (err) {
            // not logged in — keep local saved ids
        }
    };

    const loadSavedSearchesFromApi = async () => {
        try {
            const [searchesRes, alertsRes] = await Promise.all([
                OCApi.getSavedSearches(),
                OCApi.getAlertHistory({ page: 1, limit: 20 }),
            ]);
            setSavedSearches(searchesRes.savedSearches || []);
            setAlertHistory(alertsRes.alerts || []);
        } catch (err) {
            // not logged in
        }
    };

    const markAlertRead = async (alertId) => {
        await OCApi.markAlertRead(alertId);
        setAlertHistory(prev => prev.map(row => row.id === alertId ? { ...row, readAt: new Date().toISOString() } : row));
    };

    const setAlertsPaused = async (alertsPaused) => {
        const data = await OCApi.updateProfile({ alertsPaused });
        setUser(u => ({ ...u, alertsPaused: data.user.alertsPaused }));
        showToast(alertsPaused ? 'All email alerts paused' : 'Email alerts resumed', 'bell');
    };

    const saveSearch = async ({ name, filters, alertEnabled = true }) => {
        if (!authed) {
            showToast('Log in to save searches', 'bell');
            go('login');
            return null;
        }
        const data = await OCApi.createSavedSearch({ name, filters, alertEnabled });
        const row = data.savedSearch;
        setSavedSearches(prev => [row, ...prev]);
        showToast('Search saved — email alerts on', 'bell');
        return row;
    };

    const toggleSavedSearchAlert = async (id, alertEnabled) => {
        const data = await OCApi.updateSavedSearch(id, { alertEnabled });
        setSavedSearches(prev => prev.map(row => row.id === id ? data.savedSearch : row));
        showToast(alertEnabled ? 'Alerts enabled' : 'Alerts paused', 'bell');
    };

    const removeSavedSearch = async (id) => {
        await OCApi.deleteSavedSearch(id);
        setSavedSearches(prev => prev.filter(row => row.id !== id));
        showToast('Saved search removed', 'check');
    };

    const loadApplicationsFromApi = async () => {
        try {
            const data = await OCApi.getApplications();
            const rows = (data.applications || []).map(OCApi.mapApplication).filter(Boolean);
            setApplied(rows);
            mergeOpportunities(rows.map(row => OCApi.mapOpportunity({
                publicId: row.id,
                id: row.opportunityId,
                title: row.title,
                organization: row.org,
                locationRaw: row.location,
                applyUrl: row.applyUrl,
                categorySlug: row.category,
                deadline: row.deadline,
            })).filter(o => o.id));
        } catch (err) {
            // not logged in or API unavailable
        }
    };

    const restoreSession = async () => {
        try {
            const data = await OCApi.getMe();
            setAuthed(true);
            setUser({
                name: data.user.displayName || data.user.email,
                email: data.user.email,
                role: data.user.role,
                alertsPaused: data.user.alertsPaused || false,
            });
            if (isEmployerRole(data.user.role)) setEmployerMode(true);
            await Promise.all([loadSavedFromApi(), loadApplicationsFromApi(), loadSavedSearchesFromApi()]);
        } catch (err) {
            setAuthed(false);
        } finally {
            setAuthReady(true);
        }
    };

    useEffect(() => {
        (async () => {
            try {
                const [catRes, srcRes] = await Promise.all([
                    OCApi.getCategories(),
                    OCApi.getSources(),
                ]);
                setCategories(catRes.categories || []);
                const sources = srcRes.sources || [];
                const manual = sources.find(s => s.slug === 'manual') || sources[0];
                if (manual) setDefaultSourceId(manual.id);
            } catch (err) {
                console.warn('Bootstrap metadata failed:', err.message);
            }
            await restoreSession();
            await loadOpportunities({ limit: 24 });
        })();
    }, []);

    const login = async (email, password) => {
        await OCApi.login(email, password);
        const me = await OCApi.getMe();
        setAuthed(true);
        setUser({
            name: me.user.displayName || me.user.email,
            email: me.user.email,
            role: me.user.role,
            alertsPaused: me.user.alertsPaused || false,
        });
        if (isEmployerRole(me.user.role)) setEmployerMode(true);
        await loadSavedFromApi();
        await loadApplicationsFromApi();
        await loadSavedSearchesFromApi();
        showToast('Welcome back', 'check');
    };

    const signup = async (email, password, displayName, registerAsEmployer = false) => {
        await OCApi.signup(email, password, displayName, registerAsEmployer);
        await OCApi.login(email, password);
        const me = await OCApi.getMe();
        setAuthed(true);
        setUser({
            name: me.user.displayName || displayName || email,
            email: me.user.email,
            role: me.user.role,
            alertsPaused: me.user.alertsPaused || false,
        });
        if (isEmployerRole(me.user.role)) {
            setEmployerMode(true);
        }
        await loadApplicationsFromApi();
        await loadSavedSearchesFromApi();
        showToast(registerAsEmployer ? 'Employer account created' : 'Account created', 'check');
    };

    const registerAsEmployer = async () => {
        const data = await OCApi.registerAsEmployer();
        setUser(u => ({ ...u, role: data.user.role }));
        setEmployerMode(true);
        showToast('Switched to employer account', 'briefcase');
        return data.user;
    };

    const logout = async () => {
        try { await OCApi.logout(); } catch (e) { /* ignore */ }
        setAuthed(false);
        setEmployerMode(false);
        setUser({ name: '', email: '', alertsPaused: false });
        setSaved([]);
        setSavedDbMap({});
        setApplied([]);
        setSavedSearches([]);
        setAlertHistory([]);
        setEmployerProfile(null);
        setEmployerListings([]);
        setEmployerApplicants([]);
        setEmployerStats(null);
    };

    const go = (name, params = {}) => {
        if ((name === 'employers' || name === 'postJob') && !authed) {
            showToast('Log in to access employer tools', 'x');
            name = 'login';
            params = {};
        } else if ((name === 'employers' || name === 'postJob') && authed && !isEmployerRole(user.role)) {
            showToast('Switch to an employer account in Settings', 'briefcase');
            name = 'account';
            params = { tab: 'settings' };
        }
        setRoute({ name, ...params });
        window.scrollTo({ top: 0, behavior: 'auto' });
        if (name === 'companyProfile') {
        window.history.pushState({}, '', '/company-profile');
    }
    if (name === 'about') {
    window.history.pushState({}, '', '/about');
}
        if (name === 'home') {
            window.history.pushState({}, '', '/');
        } else if (name === 'feed') {
            window.history.pushState({}, '', '/feed');
        } else if (name === 'search') {
            window.history.pushState({}, '', '/search');
        } else if (name === 'saved') {
            window.history.pushState({}, '', '/saved');
        } else if (name === 'applications') {
            window.history.pushState({}, '', '/applications');
        } else if (name === 'login') {
            window.history.pushState({}, '', '/auth?mode=login');
        } else if (name === 'signup') {
            window.history.pushState({}, '', '/auth?mode=signup');
        } else if (name === 'account') {
            window.history.pushState({}, '', '/account');
        } else if (name === 'employers') {
            window.history.pushState({}, '', '/employers');
        } else if (name === 'postJob') {
            window.history.pushState({}, '', '/post-job');
        } else if (name === 'detail') {
            window.history.pushState({}, '', `/opportunity/${params.id || ''}`);
        } else if (name === 'admin') {
            window.history.pushState({}, '', '/admin');
        } else if (name === 'notifications') {
            window.history.pushState({}, '', '/alerts');
        } else if (name === 'community') {
            window.history.pushState({}, '', '/community');
        } else if (name === 'resources') {
            window.history.pushState({}, '', '/resources');
        } else if (name === 'guideDetail') {
            window.history.pushState({}, '', `/resources/${params.slug || ''}`);
        } else if (name === 'support') {
            const slug = params.supportSlug || 'help';
            window.history.pushState({}, '', `/${slug}`);
        }
    };

    const setTheme = (v) => setThemeState(v);
    const toggleTheme = () => setThemeState(t => (t === 'light' ? 'dark' : 'light'));

    const isSaved = (id) => saved.includes(id);
    const resolveDbId = (publicId) => {
        const cached = oppCache[publicId];
        if (cached && cached.dbId) return cached.dbId;
        return savedDbMap[publicId];
    };

    const toggleSave = async (id) => {
        if (!authed) {
            showToast('Log in to save opportunities', 'bookmark');
            go('login');
            return;
        }
        const has = saved.includes(id);
        const dbId = resolveDbId(id);
        if (!dbId) {
            showToast('Reload this listing and try again', 'x');
            return;
        }
        try {
            if (has) {
                await OCApi.unsaveOpportunity(dbId);
                setSaved(s => s.filter(x => x !== id));
                showToast('Removed from saved', 'bookmark');
            } else {
                await OCApi.saveOpportunity(dbId);
                setSaved(s => [id, ...s]);
                setSavedDbMap(m => ({ ...m, [id]: dbId }));
                showToast('Saved to your list', 'bookmarkFilled');
            }
        } catch (err) {
            showToast(err.message || 'Could not update saved list', 'x');
        }
    };

    const findOpportunity = (id) => oppCache[id] || opportunities.find(o => o.id === id);

    const hasApplied = (id) => applied.some(a => a.id === id);

    const markApplied = async (id, options = {}) => {
        if (hasApplied(id)) return;

        setApplied(a => [{ id, status: 'applied', date: 'Just now', applyChannel: options.applyChannel || 'external' }, ...a]);

        if (!authed) return;

        try {
            const payload = {
                publicId: id,
                applyChannel: options.applyChannel || 'external',
            };
            if (options.coverLetter) payload.coverLetter = options.coverLetter;
            if (options.resumeStorageKey) payload.resumeStorageKey = options.resumeStorageKey;

            const data = await OCApi.createApplication(payload);
            const mapped = OCApi.mapApplication(data.application);
            if (mapped) {
                setApplied(a => a.map(x => x.id === id ? mapped : x));
                mergeOpportunities([OCApi.mapOpportunity({
                    id: mapped.opportunityId,
                    publicId: mapped.id,
                    title: mapped.title,
                    organization: mapped.org,
                    locationRaw: mapped.location,
                    applyUrl: mapped.applyUrl,
                    categorySlug: mapped.category,
                    deadline: mapped.deadline,
                })]);
            }
        } catch (err) {
            setApplied(a => a.filter(x => x.id !== id));
            showToast(err.message || 'Could not save application', 'x');
            throw err;
        }
    };

    const submitInternalApplication = async (id, { coverLetter, resumeFile }) => {
        if (!resumeFile) throw new Error('Please attach your resume');
        const upload = await OCApi.uploadResume(resumeFile);
        await markApplied(id, {
            coverLetter: coverLetter || undefined,
            resumeStorageKey: upload.storageKey,
            applyChannel: 'internal',
        });
    };

    const setApplyStatus = async (publicId, status) => {
        const entry = applied.find(x => x.id === publicId);
        setApplied(a => a.map(x => x.id === publicId ? { ...x, status } : x));

        if (!authed || !entry?.applicationId) return;

        try {
            await OCApi.updateApplication(entry.applicationId, { status });
        } catch (err) {
            showToast(err.message || 'Could not update status', 'x');
            await loadApplicationsFromApi();
        }
    };

    const removeApplication = async (publicId) => {
        const entry = applied.find(x => x.id === publicId);
        setApplied(a => a.filter(x => x.id !== publicId));

        if (!authed || !entry?.applicationId) return;

        try {
            await OCApi.deleteApplication(entry.applicationId);
            showToast('Removed from tracker', 'check');
        } catch (err) {
            showToast(err.message || 'Could not remove application', 'x');
            await loadApplicationsFromApi();
        }
    };

    const loadEmployerDashboard = async () => {
        if (!authed) {
            setEmployerProfile(null);
            setEmployerListings([]);
            setEmployerApplicants([]);
            setEmployerStats(null);
            return;
        }
        setEmployerLoading(true);
        try {
            const [profileRes, listingsRes, applicantsRes, statsRes] = await Promise.all([
                OCApi.getEmployerProfile(),
                OCApi.getEmployerListings(),
                OCApi.getEmployerApplicants(),
                OCApi.getEmployerStats(),
            ]);
            setEmployerProfile(profileRes.profile ? OCApi.mapEmployerProfile(profileRes.profile) : null);
            setEmployerListings((listingsRes.listings || []).map(OCApi.mapEmployerListing).filter(Boolean));
            setEmployerApplicants((applicantsRes.applicants || []).map(OCApi.mapEmployerApplicant).filter(Boolean));
            setEmployerStats(statsRes.stats || null);
        } catch (err) {
            showToast(err.message || 'Could not load employer dashboard', 'x');
        } finally {
            setEmployerLoading(false);
        }
    };

    const saveEmployerProfile = async (payload) => {
        const data = await OCApi.saveEmployerProfile(payload);
        const profile = OCApi.mapEmployerProfile(data.profile);
        setEmployerProfile(profile);
        showToast('Company profile submitted for review', 'check');
        return profile;
    };

    const updateEmployerApplicantStatus = async (id, status) => {
        await OCApi.updateEmployerApplicant(id, { status });
        setEmployerApplicants(prev => prev.map(row => row.id === id ? { ...row, status } : row));
    };

    const toggleSetting = (key) => setSettings(s => ({ ...s, [key]: !s[key] }));
    const toggleInterest = (key) => setInterests(s => s.includes(key) ? s.filter(x => x !== key) : [...s, key]);

    const submitStagedOpportunity = async ({ title, organization, description, categorySlug, applyUrl, isRemote, city, deadline, detailPayload }) => {
        if (!authed) {
            showToast('Log in to post opportunities', 'x');
            go('login');
            throw new Error('Login required');
        }
        if (!isEmployerRole(user.role)) {
            showToast('Employer account required', 'x');
            throw new Error('Employer role required');
        }
        if (employerProfile && employerProfile.verificationStatus !== 'verified') {
            showToast('Company profile must be verified before posting', 'x');
            go('employers', { tab: 'profile' });
            throw new Error('Profile not verified');
        }
        const cat = categories.find(c => c.slug === categorySlug);
        if (!cat) throw new Error('Category not configured in database');
        if (!defaultSourceId) throw new Error('No source configured — add a manual source in the database');
        return OCApi.createStaged({
            title,
            organization,
            description,
            categoryId: cat.id,
            sourceId: defaultSourceId,
            applyUrl: applyUrl || undefined,
            isRemote: !!isRemote,
            city: city || undefined,
            deadline: deadline || undefined,
            detailPayload: detailPayload || undefined,
        });
    };

    const startApply = (id) => {
        const op = findOpportunity(id);
        const isInternal = op?.isInternalApply || op?.applicationType === 'Internal Apply';
        if (!isInternal && op?.applyUrl) {
            window.open(op.applyUrl, '_blank', 'noopener,noreferrer');
        }
        setApply({ id, mode: isInternal ? 'internal' : 'external' });
    };
    const advanceApply = () => setApply(a => a ? { ...a, step: 'open' } : null);
    const closeApply = () => setApply(null);
    const confirmApplied = async () => {
        if (apply) {
            await markApplied(apply.id);
            showToast('Added to your tracker', 'check');
        }
        setApply(null);
    };

    const value = {
        theme, setTheme, toggleTheme,
        route, go,
        saved, isSaved, toggleSave, savedDbMap,
        applied, hasApplied, markApplied, setApplyStatus, removeApplication, loadApplicationsFromApi,
        interests, toggleInterest,
        settings, toggleSetting, setSettings,
        authed, authReady, setAuthed, user, setUser,
        login, signup, logout,
        query, setQuery,
        toast, showToast,
        apply, startApply, advanceApply, closeApply, confirmApplied, submitInternalApplication,
        isEmployerMode, setEmployerMode,
        opportunities, oppLoading, oppRefreshing, oppLoadError, oppCache, loadOpportunities, fetchOpportunity, findOpportunity,
        categories, defaultSourceId, submitStagedOpportunity, registerAsEmployer, isEmployerRole,
        savedSearches, alertHistory, saveSearch, toggleSavedSearchAlert, removeSavedSearch, loadSavedSearchesFromApi, markAlertRead, setAlertsPaused,
        employerProfile, employerListings, employerApplicants, employerStats, employerLoading,
        loadEmployerDashboard, saveEmployerProfile, updateEmployerApplicantStatus,
    };

    return React.createElement(StoreContext.Provider, { value }, children);
}

export { StoreProvider, useStore };
